/**
 * AuthCallbackPage — SNS OAuth 콜백 핸들러
 * 진입: {origin}/auth/callback/:provider?code=...&state=...
 * 처리: code+state → POST /gatepass/social/login/{provider}
 *
 * 팝업 모드(window.opener 존재): 결과를 postMessage로 opener(SnsLoginPage)에 전달하고 창을 닫는다.
 * 팝업 차단 등으로 opener가 없는 경우(폴백): 기존처럼 이 페이지가 직접 setAuth/navigate까지 처리한다.
 *   SOCIAL_LOGIN_SUCCESS  → setAuth → /app
 *   SOCIAL_NEED_REGISTER  → /signup/sns (snsLinkToken, 가입 정보 전달)
 *   SOCIAL_REJECTED       → /login (error 상태 전달)
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@indipia/shared';
import { socialLogin, type SnsProvider } from '../api/social-auth';
import { applySnsLoginResult, SNS_AUTH_MESSAGE_TYPE, type SnsAuthMessage } from '../lib/sns-auth';

const OAUTH_STATE_KEY = 'oauth_state';

function SpinnerPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
        <svg
          className="animate-spin"
          style={{ width: 40, height: 40, color: 'var(--shell-point)' }}
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        <p className="text-body-sm text-text-secondary">
          로그인 처리 중…
        </p>
      </div>
    </div>
  );
}

export function AuthCallbackPage() {
  const { provider } = useParams<{ provider: string }>();
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const setAuth     = useAuthStore((s) => s.setAuth);
  const called   = useRef(false); // React StrictMode 이중 실행 방지

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    const opener = window.opener as Window | null;

    const finish = (payload: Pick<SnsAuthMessage, 'result' | 'error'>) => {
      if (opener) {
        const message: SnsAuthMessage = { type: SNS_AUTH_MESSAGE_TYPE, ...payload };
        opener.postMessage(message, window.location.origin);
        window.close();
        return;
      }
      if (payload.error) {
        void navigate('/login', { replace: true, state: { snsError: payload.error } });
        return;
      }
      if (payload.result) {
        applySnsLoginResult(payload.result, navigate, setAuth, queryClient);
      }
    };

    void (async () => {
      const params  = new URLSearchParams(window.location.search);
      const code    = params.get('code');
      const state   = params.get('state');
      const errParam = params.get('error');

      // OAuth 공급자에서 에러 반환 (사용자 취소 등)
      if (errParam) {
        finish({ error: '소셜 로그인이 취소되었습니다.' });
        return;
      }

      if (!code || !state || !provider) {
        finish({ error: '잘못된 OAuth 응답입니다.' });
        return;
      }

      // CSRF state 검증
      const savedState = sessionStorage.getItem(OAUTH_STATE_KEY);
      sessionStorage.removeItem(OAUTH_STATE_KEY);
      if (!savedState || savedState !== state) {
        finish({ error: '보안 인증에 실패했습니다. 다시 시도해 주세요.' });
        return;
      }

      const redirectUri = `${window.location.origin}/auth/callback/${provider}`;

      try {
        const result = await socialLogin(provider as SnsProvider, { code, redirectUri, state });
        finish({ result });
      } catch {
        finish({ error: '로그인 처리 중 오류가 발생했습니다. 다시 시도해 주세요.' });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <SpinnerPage />;
}
